// A trigger's chain in the roll log (issue 1500; docs/MRCS/workbench.md §6): the table started it itself, when a
// creature carrying an entry started When… met its event. The DM reads the record whole, answers what it asks the DM,
// and settles each damage it resolves (Apply, Change, Drop) when the table does not apply damage at once; a player is
// sent only a public record's lines (`playerTrigger`), with the question when it is theirs to answer — and, for a
// creature that is not the party's, only "<Creature>: a rule fired" (Tom's C on #1500).
import { useState } from 'react';
import { conditionWords, stopLineText, triggerDealt, triggerLines, type PlayerTrigger, type RollRecord } from '@dragons-table/vtt-shared';
import { AskButtons } from './AttackParts';
import { useTableState } from '../../table/TableState';
import { BTN } from '../ui';

const btn = BTN;

/** The DM's parts of a trigger's record: the question when it asks the DM, and each damage waiting to be settled. */
export function TriggerRows({ roll }: { roll: RollRecord }) {
  const t = roll.trigger;
  if (!t) return null;
  return (
    <>
      {t.ask && t.ask.owners.length === 0 && (t.ask.number
        ? <AskNumber rollId={roll.id} question={t.ask.question} />
        : <AskButtons rollId={roll.id} name={t.ask.name} outcomes={t.ask.outcomes} question={t.ask.question} labels={t.ask.labels} />)}
      {t.damage.map((d, i) => (d.settled ? null : <TriggerDamage key={`${roll.id}-${i}`} rollId={roll.id} target={d.onto} name={t.sides[d.onto]?.name ?? t.named?.[d.onto] ?? 'the creature'} amount={triggerDealt(t, d).total} lines={triggerDealt(t, d).lines.map(stopLineText)} kind={d.kind} heal={d.heal} />))}
      {t.conditions?.map((c, i) => (c.settled ? null : <TriggerCondition key={`${roll.id}-c${i}`} rollId={roll.id} index={i} target={c.onto} name={c.name ?? t.sides[c.onto]?.name ?? t.named?.[c.onto] ?? 'the creature'} what={`${conditionWords(c.key)}${c.rounds ? ` for ${c.rounds} round${c.rounds === 1 ? '' : 's'}` : ''}`} />))}
    </>
  );
}

/** A number the table does not know (qa-runs 264), the DM's alone: "Ogre's Strength?" and a box for a whole number. */
function AskNumber({ rollId, question }: { rollId: string; question: string }) {
  const { act, setNotice } = useTableState();
  const [value, setValue] = useState('');
  const [sent, setSent] = useState(false);
  return (
    <form className="mt-1 flex flex-wrap items-center gap-1 font-sans" data-testid={`trigger-number-${rollId}`}
      onSubmit={(e) => {
        e.preventDefault();
        const n = Math.trunc(Number(value));
        if (value.trim() === '' || !Number.isFinite(n)) return;
        setSent(true);
        act({ kind: 'attack.answer', rollId, choice: String(n) } as never, (reply) => { if (!reply.ok) { setSent(false); setNotice(reply.reason ?? 'That could not be answered.'); } });
      }}>
      <span className="text-amber-300">{question}</span>
      <input aria-label={question} type="number" step={1} className="w-16 rounded bg-stone-800 px-1 py-0.5 text-xs" value={value} onChange={(e) => setValue(e.target.value)} />
      <button type="submit" className={btn} disabled={sent}>Answer</button>
    </form>
  );
}

/** A condition a rule resolved, waiting for the DM: Apply puts it on the creature, Drop leaves it off. */
function TriggerCondition({ rollId, index, target, name, what }: { rollId: string; index: number; target: string; name: string; what: string }) {
  const { act, setNotice } = useTableState();
  const send = (n: number | null) => act({ kind: 'save.apply', rollId, target, amount: n, condition: index } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be applied.'); });
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 font-sans" data-testid={`trigger-condition-${rollId}-${target}`}>
      <span className="text-amber-300">{what} on {name}?</span>
      <button type="button" className={btn} onClick={() => send(1)}>Apply</button>
      <button type="button" className={btn} onClick={() => send(null)}>Drop</button>
    </div>
  );
}

function TriggerDamage({ rollId, target, name, amount, lines, kind, heal }: { rollId: string; target: string; name: string; amount: number; lines: string[]; kind?: string | undefined; heal?: true | undefined }) {
  const { act, setNotice } = useTableState();
  const [changing, setChanging] = useState(false);
  const [value, setValue] = useState('');
  const send = (n: number | null) => act({ kind: 'save.apply', rollId, target, amount: n } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be applied.'); });
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 font-sans" data-testid={`trigger-damage-${rollId}-${target}`}>
      <span className="text-amber-300">{heal ? `Heal ${name} ${amount}?` : `${amount}${kind ? ` ${kind}` : ''} damage to ${name}?${lines.length ? ` (${lines.join('; ')})` : ''}`}</span>
      <button type="button" className={btn} onClick={() => send(amount)}>Apply {amount}</button>
      {!changing && <button type="button" className={btn} onClick={() => { setValue(String(amount)); setChanging(true); }}>Change…</button>}
      {changing && (
        <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); const n = Math.trunc(Number(value)); if (value.trim() !== '' && Number.isFinite(n) && n >= 0) send(n); }}>
          <input aria-label={`Damage to apply to ${name}`} type="number" min={0} className="w-14 rounded bg-stone-800 px-1 py-0.5 text-xs" value={value} onChange={(e) => setValue(e.target.value)} />
          <button type="submit" className={btn}>Apply</button>
        </form>
      )}
      <button type="button" className={btn} aria-label={`Drop the damage to ${name}`} onClick={() => send(null)}>Drop</button>
    </div>
  );
}

/** The DM's text of a trigger's record (the log line and what a screen reader is told). */
export const triggerText = (roll: RollRecord): string => (roll.trigger ? triggerLines(roll.trigger).join('\n') : '');

/** A player's line for a trigger's chain: what they may know of it, and the buttons when it asks them. */
export function PlayerTriggerEntry({ trigger }: { trigger: PlayerTrigger }) {
  const [head, ...rest] = trigger.lines;
  return (
    <li className="vtt-log-card min-w-0 text-xs" data-testid={`trigger-${trigger.id}`}>
      <div className="whitespace-pre-wrap break-words">
        <span className="font-semibold text-amber-200">{head}</span>
        {rest.length > 0 && <span className="text-stone-400">{`\n${rest.join('\n')}`}</span>}
      </div>
      {trigger.ask && <AskButtons rollId={trigger.id} name={head ?? ''} outcomes={trigger.ask.outcomes} question={trigger.ask.question} labels={trigger.ask.labels} />}
      {trigger.waiting && <div className="font-sans text-[0.625rem] text-stone-400">Waiting for {trigger.waiting}.</div>}
    </li>
  );
}
